<template>
  <div
    class="crm-gsearch"
    :class="{ 'crm-gsearch--open': isOpen }"
  >
    <!-- Input -->
    <div class="crm-gsearch__box">
      <feather-icon
        icon="SearchIcon"
        size="16"
        class="crm-gsearch__box-icon"
      />
      <input
        ref="input"
        v-model="query"
        type="search"
        class="crm-gsearch__input"
        :placeholder="placeholder"
        autocomplete="off"
        spellcheck="false"
        aria-label="Search the CRM by CR number, company name or phone"
        @focus="onFocus"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.enter.prevent="chooseActive"
        @keydown.esc.prevent="onEscape"
        @keydown.tab="close"
      >
      <!-- The spinner replaces the shortcut hint so the box never shows two
           things in the same slot. -->
      <span
        v-if="loading"
        class="crm-gsearch__spinner"
      />
      <button
        v-else-if="query"
        type="button"
        class="crm-gsearch__clear"
        title="Clear"
        @click="reset(true)"
      >
        <feather-icon
          icon="XIcon"
          size="14"
        />
      </button>
      <kbd
        v-else
        class="crm-gsearch__kbd"
      >{{ shortcutHint }}</kbd>
    </div>

    <!-- Results panel. position:fixed on purpose — the CRM sidebar sets
         overflow:hidden, so an absolutely-positioned dropdown would be clipped
         by it. The coordinates come from the input's bounding rect. -->
    <div
      v-if="isOpen"
      class="crm-gsearch__panel"
      :style="panelStyle"
    >
      <!-- Too short -->
      <div
        v-if="tooShort"
        class="crm-gsearch__state"
      >
        <feather-icon
          icon="TypeIcon"
          size="18"
        />
        <p class="crm-gsearch__state-title">
          Keep typing
        </p>
        <p class="crm-gsearch__state-text">
          {{ hint || `Type at least ${minLength} characters — a CR number, a company name or a phone number.` }}
        </p>
      </div>

      <!-- Failed -->
      <div
        v-else-if="error"
        class="crm-gsearch__state crm-gsearch__state--error"
      >
        <feather-icon
          icon="AlertCircleIcon"
          size="18"
        />
        <p class="crm-gsearch__state-title">
          Search failed
        </p>
        <p class="crm-gsearch__state-text">
          {{ error }}
        </p>
        <button
          type="button"
          class="crm-gsearch__retry"
          @click="run(query)"
        >
          Try again
        </button>
      </div>

      <!-- Loading, and nothing on screen yet to keep -->
      <div
        v-else-if="loading && !hasResults"
        class="crm-gsearch__state"
      >
        <span class="crm-gsearch__spinner crm-gsearch__spinner--lg" />
        <p class="crm-gsearch__state-text">
          Searching…
        </p>
      </div>

      <!-- Nothing found -->
      <div
        v-else-if="!hasResults"
        class="crm-gsearch__state"
      >
        <feather-icon
          icon="SearchIcon"
          size="18"
        />
        <p class="crm-gsearch__state-title">
          Nothing matches "{{ lastQuery }}"
        </p>
        <p class="crm-gsearch__state-text">
          Try a CR number, company name or phone.
        </p>
        <!-- Honesty: a salesperson only ever searches their OWN opportunities and
             leads, so "not found" must not be read as "does not exist". -->
        <p
          v-if="scope !== 'all'"
          class="crm-gsearch__state-note"
        >
          Opportunities and leads are limited to what is assigned to you.
        </p>
      </div>

      <!-- Results -->
      <div
        v-else
        ref="list"
        class="crm-gsearch__list"
      >
        <div
          v-for="group in visibleGroups"
          :key="group.type"
          class="crm-gsearch__group"
        >
          <div class="crm-gsearch__group-head">
            <span class="crm-gsearch__group-label">{{ group.label }}</span>
            <span
              v-if="group.total > group.shown"
              class="crm-gsearch__group-count"
            >showing {{ group.shown }} of {{ group.total }}{{ group.totalCapped ? '+' : '' }}</span>
            <span
              v-else
              class="crm-gsearch__group-count"
            >{{ group.total }}</span>
          </div>

          <button
            v-for="item in group.results"
            :key="`${item.type}-${item.id}`"
            type="button"
            class="crm-gsearch__item"
            :class="{ 'crm-gsearch__item--active': indexOf(item) === activeIndex }"
            :data-idx="indexOf(item)"
            @mouseenter="activeIndex = indexOf(item)"
            @click="choose(item)"
          >
            <span
              class="crm-gsearch__item-icon"
              :class="`crm-gsearch__item-icon--${item.type}`"
            >
              <feather-icon
                :icon="iconFor(item.type)"
                size="15"
              />
            </span>
            <span class="crm-gsearch__item-body">
              <span class="crm-gsearch__item-label">{{ item.label }}</span>
              <span class="crm-gsearch__item-sub">{{ item.subtitle }}</span>
            </span>
            <span
              v-if="item.badge"
              class="crm-gsearch__item-badge"
            >{{ item.badge }}</span>
          </button>
        </div>

        <div class="crm-gsearch__foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
          <span><kbd>↵</kbd> open</span>
          <span><kbd>esc</kbd> close</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import decryptData from '@/store/auth/decryptData'

/**
 * BRD US 6.2 / MID-CRM X6 — the sticky global search box.
 *
 * One call to `GET crm/search?q=` returns companies, contacts, opportunities and
 * leads already grouped, capped and route-annotated, so this component does no
 * business logic of its own: it debounces, renders, and navigates to the route the
 * server handed back. That is deliberate — the routes and the access scoping are
 * decided in one place (CrmSearchController) instead of being re-derived here.
 */
export default {
  name: 'GlobalSearch',
  props: {
    placeholder: {
      type: String,
      default: 'Search CR, company, phone…',
    },
  },
  data() {
    return {
      query: '',
      // What the last COMPLETED response was for. Used in the empty state so it
      // never says 'Nothing matches "abc"' while the user has already typed "abcd".
      lastQuery: '',
      groups: [],
      loading: false,
      error: null,
      tooShort: false,
      hint: null,
      minLength: 2,
      scope: 'own',
      isOpen: false,
      activeIndex: -1,
      panelStyle: {},
      debounceTimer: null,
      // Monotonic id of the in-flight request. A slow response for an older term
      // must not overwrite the results of a newer one (the classic search race).
      requestId: 0,
    }
  },
  computed: {
    visibleGroups() {
      return this.groups.filter(g => g.results && g.results.length)
    },
    /** Every result in render order — this is what up/down walks. */
    flatResults() {
      return this.visibleGroups.reduce((acc, g) => acc.concat(g.results), [])
    },
    hasResults() {
      return this.flatResults.length > 0
    },
    shortcutHint() {
      // navigator.platform is deprecated but still the only thing that works in
      // every browser this admin panel is opened in.
      const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '')
      return mac ? '⌘K' : 'Ctrl K'
    },
  },
  watch: {
    query(value) {
      this.schedule(value)
    },
    // Navigating away must not leave a stale dropdown floating over the new page.
    $route() {
      this.close()
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.onDocumentClick)
    document.addEventListener('keydown', this.onGlobalKey)
    window.addEventListener('resize', this.positionPanel)
    // `true` = capture, so we reposition even when an inner element scrolls.
    window.addEventListener('scroll', this.positionPanel, true)
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocumentClick)
    document.removeEventListener('keydown', this.onGlobalKey)
    window.removeEventListener('resize', this.positionPanel)
    window.removeEventListener('scroll', this.positionPanel, true)
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  },
  methods: {
    /* ── input ────────────────────────────────────────────────────────── */

    /**
     * 250ms debounce. Long enough that a normal typist produces one request per
     * word instead of one per keystroke — this endpoint runs LIKE scans against
     * the shared live database, so every saved call matters.
     */
    schedule(value) {
      if (this.debounceTimer) clearTimeout(this.debounceTimer)

      const term = (value || '').trim()

      if (!term) {
        this.reset(false)
        return
      }

      if (term.length < this.minLength) {
        // Answer locally instead of asking the server to tell us it is too short.
        this.groups = []
        this.error = null
        this.tooShort = true
        this.loading = false
        this.open()
        return
      }

      this.debounceTimer = setTimeout(() => this.run(term), 250)
    },

    async run(rawTerm) {
      const term = (rawTerm || '').trim()
      if (term.length < this.minLength) return

      // eslint no-plusplus: the project forbids ++, so step it explicitly.
      this.requestId += 1
      const id = this.requestId
      this.loading = true
      this.error = null
      this.open()

      try {
        const res = await axios.get('crm/search', { params: { q: term } })
        // A response for a term the user has already moved on from is dropped.
        if (id !== this.requestId) return

        const body = this.unwrap(res)
        if (!body || body.success === false || !body.data) {
          throw new Error((body && body.message) || 'Unexpected response')
        }

        const { data } = body
        this.groups = Array.isArray(data.groups) ? data.groups : []
        this.tooShort = !!data.tooShort
        this.hint = data.hint || null
        this.minLength = data.minLength || this.minLength
        this.scope = data.scope || this.scope
        this.lastQuery = data.query || term
        this.activeIndex = this.hasResults ? 0 : -1
      } catch (e) {
        if (id !== this.requestId) return
        this.groups = []
        this.activeIndex = -1
        this.error = 'Search is unavailable right now. Check your connection and try again.'
      } finally {
        if (id === this.requestId) {
          this.loading = false
          this.positionPanel()
        }
      }
    },

    /**
     * The API encrypts every response except when the local `Encryption: test`
     * header is used, so accept both shapes rather than crashing on the plain one.
     */
    unwrap(res) {
      const payload = res && res.data ? res.data.payload : null
      return payload ? decryptData(payload) : (res && res.data)
    },

    /* ── open / close ─────────────────────────────────────────────────── */

    open() {
      this.isOpen = true
      this.$nextTick(this.positionPanel)
    },

    close() {
      this.isOpen = false
      this.activeIndex = -1
    },

    /**
     * Escape closes the dropdown first, and only gives up focus if it was already
     * closed — so one press never both closes and blurs.
     */
    onEscape() {
      if (this.isOpen) {
        this.close()
        return
      }
      this.reset(false)
      if (this.$refs.input) this.$refs.input.blur()
    },

    reset(focus) {
      if (this.debounceTimer) clearTimeout(this.debounceTimer)
      // Bump the request id so an in-flight response cannot repopulate the list.
      this.requestId += 1
      this.query = ''
      this.groups = []
      this.error = null
      this.tooShort = false
      this.loading = false
      this.activeIndex = -1
      this.isOpen = false
      if (focus && this.$refs.input) this.$refs.input.focus()
    },

    onFocus() {
      if (this.query.trim().length >= this.minLength || this.tooShort) this.open()
    },

    onDocumentClick(event) {
      if (!this.isOpen) return
      if (this.$el && !this.$el.contains(event.target)) this.close()
    },

    onGlobalKey(event) {
      // Ctrl/Cmd+K focuses the box from anywhere in the CRM.
      if ((event.ctrlKey || event.metaKey) && (event.key === 'k' || event.key === 'K')) {
        event.preventDefault()
        if (this.$refs.input) {
          this.$refs.input.focus()
          this.$refs.input.select()
        }
      }
    },

    /**
     * The panel is fixed-positioned, so it needs real screen coordinates. Width is
     * the wider of the input and 340px, clamped to the viewport so it can never
     * hang off the right edge on a narrow window.
     */
    positionPanel() {
      if (!this.isOpen || !this.$refs.input) return

      const rect = this.$refs.input.getBoundingClientRect()
      const margin = 8
      const width = Math.min(Math.max(rect.width, 340), window.innerWidth - (margin * 2))
      const left = Math.min(rect.left, window.innerWidth - width - margin)

      this.panelStyle = {
        top: `${rect.bottom + 6}px`,
        left: `${Math.max(margin, left)}px`,
        width: `${width}px`,
        maxHeight: `${Math.max(180, window.innerHeight - rect.bottom - 24)}px`,
      }
    },

    /* ── keyboard navigation ──────────────────────────────────────────── */

    indexOf(item) {
      return this.flatResults.indexOf(item)
    },

    move(step) {
      if (!this.isOpen) {
        this.open()
        return
      }
      if (!this.hasResults) return

      const count = this.flatResults.length
      // Wraps, so ↑ from the first row jumps to the last.
      this.activeIndex = (this.activeIndex + step + count) % count
      this.$nextTick(this.scrollActiveIntoView)
    },

    scrollActiveIntoView() {
      const { list } = this.$refs
      if (!list) return
      const el = list.querySelector(`[data-idx="${this.activeIndex}"]`)
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
    },

    chooseActive() {
      const item = this.flatResults[this.activeIndex]
      if (item) this.choose(item)
    },

    /* ── navigation ───────────────────────────────────────────────────── */

    /**
     * The server decides the destination (see CrmSearchController): companies and
     * contacts go to their detail pages, an opportunity deep-links the pipeline
     * board (?card=&pipeline=) and a lead deep-links the leads list (?lead=).
     * `.catch()` swallows vue-router's NavigationDuplicated when the user picks the
     * row for the page they are already on.
     */
    choose(item) {
      if (!item || !item.route || !item.route.name) return

      const target = { name: item.route.name }
      if (item.route.params) target.params = item.route.params
      if (item.route.query) target.query = item.route.query

      this.reset(false)
      this.$router.push(target).catch(() => {})
    },

    iconFor(type) {
      return {
        company: 'HomeIcon',
        contact: 'UserIcon',
        opportunity: 'TrendingUpIcon',
        lead: 'ZapIcon',
        // Karim, 3 Sep: "the search result should be in all data."
        request: 'FileTextIcon',
        certificate: 'AwardIcon',
      }[type] || 'CircleIcon'
    },
  },
}
</script>

<style lang="scss" scoped>
.crm-gsearch {
  position: relative;
  width: 100%;
}

/* ── input ─────────────────────────────────────────────────────────────── */
.crm-gsearch__box {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.6rem;
  border-radius: 12px;
  background: #f7f7fb;
  border: 1px solid #e8eaf0;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.crm-gsearch--open .crm-gsearch__box,
.crm-gsearch__box:focus-within {
  background: #fff;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.crm-gsearch__box-icon {
  color: #9e9bb5;
  flex-shrink: 0;
}

.crm-gsearch__input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.84rem;
  color: #2d2d3f;

  &::placeholder {
    color: #a9a7ba;
  }

  /* Chrome's own clear "x" would sit next to ours. */
  &::-webkit-search-cancel-button {
    display: none;
  }
}

.crm-gsearch__kbd,
.crm-gsearch__foot kbd {
  font-size: 0.62rem;
  font-weight: 700;
  color: #9e9bb5;
  background: #eceaf7;
  border-radius: 5px;
  padding: 0.1rem 0.32rem;
  white-space: nowrap;
}

.crm-gsearch__clear {
  border: none;
  background: transparent;
  color: #9e9bb5;
  cursor: pointer;
  padding: 0;
  display: inline-flex;

  &:hover {
    color: #ea5455;
  }
}

.crm-gsearch__spinner {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #ded9fb;
  border-top-color: #2563eb;
  animation: crm-gsearch-spin 0.6s linear infinite;
  flex-shrink: 0;

  &--lg {
    width: 22px;
    height: 22px;
    border-width: 3px;
  }
}

@keyframes crm-gsearch-spin {
  to { transform: rotate(360deg); }
}

/* ── panel ─────────────────────────────────────────────────────────────── */
.crm-gsearch__panel {
  position: fixed;
  z-index: 1080; /* above the CRM sidebar (1000) */
  background: #fff;
  border: 1px solid #e8eaf0;
  border-radius: 14px;
  box-shadow: 0 18px 48px rgba(43, 41, 71, 0.16);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.crm-gsearch__list {
  overflow-y: auto;
  padding: 0.35rem 0;
}

.crm-gsearch__group + .crm-gsearch__group {
  border-top: 1px solid #f2f2f7;
}

.crm-gsearch__group-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 0.5rem 0.85rem 0.25rem;
}

.crm-gsearch__group-label {
  font-size: 0.63rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: #b0adc8;
}

.crm-gsearch__group-count {
  font-size: 0.66rem;
  font-weight: 600;
  color: #b0adc8;
}

.crm-gsearch__item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 0.45rem 0.85rem;
  cursor: pointer;

  &--active {
    background: #f5f4ff;
  }
}

.crm-gsearch__item-icon {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #f0eefe;
  color: #2563eb;

  &--contact { background: #e6f8fb; color: #00cfe8; }
  &--opportunity { background: #e8f8ef; color: #28c76f; }
  &--lead { background: #e4eefe; color: #3b82f6; }
  &--request { background: #eef2ff; color: #2563eb; }
  &--certificate { background: #fdf2f8; color: #db2777; }
}

.crm-gsearch__item-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.crm-gsearch__item-label {
  font-size: 0.82rem;
  font-weight: 700;
  color: #2d2d3f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.crm-gsearch__item-sub {
  font-size: 0.7rem;
  color: #8e8ba6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.crm-gsearch__item-badge {
  font-size: 0.68rem;
  font-weight: 700;
  color: #5e5c78;
  background: #f2f2f7;
  border-radius: 6px;
  padding: 0.12rem 0.4rem;
  flex-shrink: 0;
}

/* ── states ────────────────────────────────────────────────────────────── */
.crm-gsearch__state {
  padding: 1.15rem 1rem;
  text-align: center;
  color: #9e9bb5;

  &--error { color: #ea5455; }
}

.crm-gsearch__state-title {
  margin: 0.45rem 0 0.15rem;
  font-size: 0.82rem;
  font-weight: 700;
  color: #2d2d3f;
}

.crm-gsearch__state-text {
  margin: 0;
  font-size: 0.73rem;
  line-height: 1.4;
}

.crm-gsearch__state-note {
  margin: 0.5rem 0 0;
  font-size: 0.68rem;
  color: #b0adc8;
  font-style: italic;
}

.crm-gsearch__retry {
  margin-top: 0.6rem;
  border: 1px solid #e8eaf0;
  background: #fff;
  border-radius: 8px;
  padding: 0.22rem 0.7rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #2563eb;
  cursor: pointer;
}

.crm-gsearch__foot {
  display: flex;
  gap: 0.85rem;
  padding: 0.4rem 0.85rem;
  border-top: 1px solid #f2f2f7;
  font-size: 0.66rem;
  color: #b0adc8;
  background: #fbfbfd;
}
</style>
